import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { map, withLatestFrom, filter, switchMap, mergeMap } from 'rxjs/operators';
import { GlobalConstants } from 'src/app/base/global.constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import * as propertyActions from 'src/app/STORE/actions/propertypane.actions';
import { allPropertiesData, propertiesData } from '../selectors/propertypane.selector';
import { AppGlobalService } from 'src/app/services/app.global.service';
export enum propNodeType {
  FIELD = 'FIELD',
  FIELDGROUP = 'FIELD_GROUP',
  TABLE = 'TABLE',
  GROUP = 'TABLE_GROUP',
  PROVIDER = 'provider',
  COMPONENT = 'component',
  API = 'api',
  WORKFLOWACTION = 'workflowAction',
  WORKFLOWACTOR = 'workflowActor'
}

@Injectable()
export class PropertyPaneEffects {
  private actions$ = inject(Actions);
  private baseService = inject(HttpBaseService);
  private store = inject(Store);
  private appGlobalService = inject(AppGlobalService);

  dataModelNodes = ['field', 'fieldgroup', 'group', 'table'];

  getPropertiesBasedonFetchTypeAndDB(action, properties) {
    let fetchTypeData;
    if (!properties) {
      return fetchTypeData;
    }
    if (this.dataModelNodes.includes(action.propPaneApiConstants?.fetchType)) {
      if (
        Object.prototype.hasOwnProperty.call(properties, 'sql') ||
        Object.prototype.hasOwnProperty.call(properties, 'noSql')
      ) {
        if (this.appGlobalService.get('transactionDb') === GlobalConstants.transactionDbRdbms) {
          fetchTypeData = properties.sql[action.propPaneApiConstants?.fetchType];
        } else {
          fetchTypeData = properties.noSql[action.propPaneApiConstants?.fetchType];
        }
      }
    } else {
      fetchTypeData = properties[action.propPaneApiConstants?.fetchType];
    }
    return fetchTypeData;
  }

  setFieldProps(action, dataToSet) {
    const fType = action.propPaneApiConstants?.fieldType;
    const fieldProperties = dataToSet?.properties
      ?.filter((prp) => {
        return (
          (prp?.type !== 'group' && prp?.possibleFieldTypes?.includes(fType)) ||
          (prp?.type === 'group' && prp?.items.filter((i) => i?.possibleFieldTypes?.includes(fType))?.length)
        );
      })
      .map((fprop) => {
        if (fprop?.type === 'group') {
          const grpItems = fprop?.items.filter((i) => i?.possibleFieldTypes?.includes(fType));
          fprop = { ...fprop, ...{ items: grpItems } };
        }
        return fprop;
      });
    dataToSet = { ...dataToSet, ...{ properties: fieldProperties } };
    this.store.dispatch(propertyActions.setPropertiesData({ propertiesData: dataToSet }));
  }

  getPropertyPaneData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(propertyActions.getPropertiesData),
        withLatestFrom(this.store.select(allPropertiesData)),
        mergeMap(([action, properties]) => {
          const fetchTypeData = properties ? properties[action.propPaneApiConstants?.fetchType] : [];
          if (!properties || !fetchTypeData) {
            return this.baseService[action.propPaneApiConstants?.method](action.propPaneApiConstants).pipe(
              map((propsData: any) => {
                const dataToSet = propsData[action.propPaneApiConstants?.fetchType] || [];
                this.store.dispatch(
                  propertyActions.setAllPropertiesData({ allProperties: { ...properties, ...propsData } })
                );
                let type;
                if (action.propPaneApiConstants?.fetchType) {
                  type = propNodeType[action.propPaneApiConstants?.fetchType.toUpperCase()];
                }
                if (action.propPaneApiConstants?.fieldType) {
                  this.setFieldProps(action, dataToSet);
                } else {
                  this.store.dispatch(propertyActions.setPropertiesData({ propertiesData: dataToSet }));
                }
                this.store.dispatch(propertyActions.setPropertiesType({ propertiesType: type }));
                this.store.dispatch(propertyActions.triggerLoading({ triggerPropLoading: false }));
              })
            );
          }
          const dataToSource = properties[action.propPaneApiConstants?.fetchType] || [];
          return of(dataToSource).pipe(
            map((dataToSet: any) => {
              if (action.propPaneApiConstants?.fieldType) {
                this.setFieldProps(action, dataToSet);
              } else {
                // changes for consumed rest API
                // to convert possible values to uppercase in APIINFO
                if (dataToSet.id === 'api') {
                  if (dataToSet.properties.basic) {
                    const modifyProp = dataToSet.properties.basic.map((apiprp) => {
                      if (apiprp.items) {
                        const mediaTypePrp = apiprp.items.map((item) => {
                          if (item.name === 'requestMediaType' || item.name === 'responseMediaType') {
                            const modifyItem = item.possibleValues.map((posVal) => {
                              const val = posVal.value.toUpperCase();
                              posVal = { ...posVal, value: val };
                              return posVal;
                            });
                            item = {
                              ...item,
                              ...{ possibleValues: modifyItem },
                              ...{ defaultValue: item.defaultValue.toUpperCase() }
                            };
                          }
                          if (
                            item.name === 'requestXmlSchema' ||
                            item.name === 'responseXmlSchema' ||
                            item.name === 'requestJsonSchema' ||
                            item.name === 'responseJsonSchema'
                          ) {
                            const modifyCond = item.conditionalVisiblity.map((conVis) => {
                              if (conVis.field === 'requestMediaType' || conVis.field === 'responseMediaType') {
                                const val = conVis.value.toUpperCase();
                                conVis = { ...conVis, value: val };
                              }
                              return conVis;
                            });
                            item = { ...item, ...{ conditionalVisiblity: modifyCond } };
                          }
                          return item;
                        });
                        apiprp = { ...apiprp, ...{ items: mediaTypePrp } };
                      }
                      return apiprp;
                    });
                    const updatedProperties = { basic: modifyProp, advanced: dataToSet.properties.advanced };
                    dataToSet = {
                      ...dataToSet,
                      ...{ properties: updatedProperties }
                    };
                  }
                  if (dataToSet.properties.advanced) {
                    const modifyProp = dataToSet.properties.advanced.map((apiprp) => {
                      if (apiprp.name === 'apiServiceType') {
                        const modifyItem = apiprp.possibleValues.map((posVal) => {
                          const val = posVal.value.toUpperCase();
                          posVal = { ...posVal, value: val };
                          return posVal;
                        });
                        apiprp = {
                          ...apiprp,
                          ...{ possibleValues: modifyItem },
                          ...{ defaultValue: apiprp.defaultValue.toUpperCase() }
                        };
                      }
                      return apiprp;
                    });
                    const updatedProperties = { basic: dataToSet.properties.basic, advanced: modifyProp };
                    dataToSet = {
                      ...dataToSet,
                      ...{ properties: updatedProperties }
                    };
                  }
                }
                this.store.dispatch(propertyActions.setPropertiesData({ propertiesData: dataToSet }));
              }
              let type;
              if (action.propPaneApiConstants?.fetchType) {
                type = propNodeType[action.propPaneApiConstants?.fetchType.toUpperCase()];
              }
              this.store.dispatch(
                propertyActions.setPropertiesType({
                  propertiesType: type ? type : action.propPaneApiConstants?.fetchType
                })
              );
              this.store.dispatch(propertyActions.triggerLoading({ triggerPropLoading: false }));
            })
          );
        })
      ),
    {
      dispatch: false
    }
  );

  getPropertiesValues$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(propertyActions.getPropertiesValues),
        map((action: any) => {
          return this.baseService[action.propPaneApiConstants?.method](
            action.propPaneApiConstants,
            action.params
          ).subscribe((propertiesValues: any) => {
            //console.log(propertiesValues);
            if (propertiesValues) {
              this.store.dispatch(propertyActions.setPropertiesValues({ propertiesValues: propertiesValues }));
            } else {
              this.store.dispatch(propertyActions.setPropertiesValues({ propertiesValues: 'error' }));
            }
          });
        })
      ),
    {
      dispatch: false
    }
  );

  savePropertiesValues$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(propertyActions.savePropertiesValues),
        map((action: any) => {
          return this.baseService.get(action).subscribe((propertiesValues: any) => {
            //console.log(propertiesValues);
            this.store.dispatch(propertyActions.setPropertiesValues({ propertiesValues: propertiesValues }));
          });
        })
      ),
    {
      dispatch: false
    }
  );
  updatePropertiesValues$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(propertyActions.updatePropertiesValues),
        map((action: any) => {
          return this.baseService.put(action).subscribe((propertiesValues: any) => {
            this.store.dispatch(propertyActions.setPropertiesValues({ propertiesValues: propertiesValues }));
          });
        })
      ),
    {
      dispatch: false
    }
  );

  deletePropertiesValues$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(propertyActions.deletePropertiesValues),
        map((action: any) => {
          return this.baseService.delete(action);
        })
      ),
    {
      dispatch: false
    }
  );

  getSearchableFields = createEffect(
    () =>
      this.actions$.pipe(
        ofType(propertyActions.getSearchableFields),
        map((action) =>
          this.baseService[action.apiConstants?.method](action.apiConstants, action.params).subscribe((result: any) => {
            this.store.dispatch(propertyActions.setSearchableFields({ searchFields: result }));
          })
        )
      ),
    { dispatch: false }
  );
}
